import {
  SmartReviewBankTransferGroup,
  SmartReviewTransaction,
} from "store/apis/transactions";
import { createColumnHelper, ColumnDef } from "react-table-8.10.7";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { SmartReviewTxnTable } from "./SmartReviewTxnTable";

type SelectedGroupNums = number[];

type BankTableMeta = {
  selectedGroupNums: SelectedGroupNums;
  onToggleGroup: (groupNumber: number, checked: boolean) => void;
};

type FlatBankTransferRow = SmartReviewTransaction & {
  group_number: number;
  is_first_in_group: boolean;
};

const columnHelper = createColumnHelper<FlatBankTransferRow>();

const selectColumn: ColumnDef<FlatBankTransferRow> = columnHelper.display({
  id: "select",
  size: 10,
  header: ({ table }) => {
    const { selectedGroupNums, onToggleGroup } = table.options
      .meta as BankTableMeta;
    const allGroupNums = table
      .getCoreRowModel()
      .rows.filter((r) => r.original.is_first_in_group)
      .map((r) => r.original.group_number);
    const allSelected =
      allGroupNums.length > 0 &&
      allGroupNums.every((g) => selectedGroupNums.includes(g));
    const someSelected = allGroupNums.some((g) =>
      selectedGroupNums.includes(g)
    );
    return (
      <Checkbox
        checked={allSelected}
        indeterminate={!allSelected && someSelected}
        onChange={() =>
          allGroupNums.forEach((g) => onToggleGroup(g, !allSelected))
        }
      />
    );
  },
  cell: ({ row, table }) => {
    if (!row.original.is_first_in_group) return null;
    const { selectedGroupNums, onToggleGroup } = table.options
      .meta as BankTableMeta;
    const checked = selectedGroupNums.includes(row.original.group_number);
    return (
      <Checkbox
        checked={checked}
        onChange={() => onToggleGroup(row.original.group_number, !checked)}
        onClick={(e) => e.stopPropagation()}
      />
    );
  },
}) as ColumnDef<FlatBankTransferRow>;

const groupNumberColumn: ColumnDef<FlatBankTransferRow> = columnHelper.display({
  id: "group_number",
  size: 10,
  header: () => <HeadCellContent title="#" />,
  cell: ({ row }) => {
    if (!row.original.is_first_in_group) return null;
    return (
      <span className="t-text-subtext t-text-text-30">
        {String(row.original.group_number).padStart(2, "0")}
      </span>
    );
  },
}) as ColumnDef<FlatBankTransferRow>;

type SmartReviewBankTransferTableProps = {
  groups: SmartReviewBankTransferGroup[];
  selectedGroupNums: SelectedGroupNums;
  confirmingIds?: string[];
  updatingCategoryIds?: string[];
  onToggleGroup: (groupNumber: number, checked: boolean) => void;
  onCategoryChange?: (txnId: string, categoryId: string | null) => void;
};

export const SmartReviewBankTransferTable = ({
  groups,
  selectedGroupNums,
  confirmingIds = [],
  updatingCategoryIds = [],
  onToggleGroup,
  onCategoryChange,
}: SmartReviewBankTransferTableProps) => {
  const flatRows: FlatBankTransferRow[] = groups.flatMap((group) =>
    group.transactions.map((txn, idx) => ({
      ...txn,
      group_number: group.group_number,
      is_first_in_group: idx === 0,
    }))
  );

  return (
    <SmartReviewTxnTable
      transactions={flatRows}
      selectColumn={selectColumn}
      prefixColumns={[groupNumberColumn]}
      confirmingIds={confirmingIds}
      isSelected={(txn) => {
        const row = txn as FlatBankTransferRow;
        return (
          selectedGroupNums.includes(row.group_number) && row.is_first_in_group
        );
      }}
      isRowClickable={(txn) => (txn as FlatBankTransferRow).is_first_in_group}
      onRowClick={(txn) => {
        const row = txn as FlatBankTransferRow;
        onToggleGroup(
          row.group_number,
          !selectedGroupNums.includes(row.group_number)
        );
      }}
      meta={{
        selectedGroupNums,
        onToggleGroup,
        onCategoryChange,
        updatingCategoryIds,
      }}
    />
  );
};
